import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { Environment } from "@react-three/drei";
import * as THREE from "three";
import { getNumberColor } from "../utils/gameLogic";
import { sounds } from "../utils/audio";
import "./Game.css";

// European Roulette sequence (37 pockets)
const EUROPEAN_WHEEL = [
  0, 32, 15, 19, 4, 21, 2, 25, 17, 34, 6, 27, 13, 36, 11, 30, 8, 23,
  10, 5, 24, 16, 33, 1, 20, 14, 31, 9, 22, 18, 29, 7, 28, 12, 35, 3, 26
];

const AMERICAN_WHEEL = [
  0, 28, 9, 26, 30, 11, 7, 20, 32, 17, 5, 22, 34, 15, 3, 24, 36, 13, 1,
  "00", 27, 10, 25, 29, 12, 8, 19, 31, 18, 6, 21, 33, 16, 4, 23, 35, 14, 2
];

const RED_NUMBERS = new Set([
  1, 3, 5, 7, 9, 12, 14, 16, 18, 19, 21, 23, 25, 27, 30, 32, 34, 36
]);

const TAU = Math.PI * 2;

function getPocketColor(num) {
  if (num === 0 || num === "00") return "#008544"; // Emerald Green
  const n = Number(num);
  if (RED_NUMBERS.has(n)) return "#ff0000"; // Full Pure Red
  return "#000000"; // Full Pure Black
}

// Generate unified, photorealistic 2048x2048 texture for the entire numbered rotor ring
function createPhotorealisticRotorTexture(sequence) {
  const canvas = document.createElement("canvas");
  canvas.width = 2048;
  canvas.height = 2048;
  const ctx = canvas.getContext("2d");

  const cx = 1024;
  const cy = 1024;
  const total = sequence.length;
  const step = TAU / total;

  const rOuter = 1024;
  const rInner = 490;
  const rText = 940;

  ctx.clearRect(0, 0, 2048, 2048);

  // 1. Draw each pocket slice seamlessly (single continuous pocket with its true color)
  for (let i = 0; i < total; i++) {
    const num = sequence[i];
    const startAngle = i * step - step / 2;
    const endAngle = i * step + step / 2;
    const midAngle = i * step;

    // A. Entire Pocket Surface (Crimson Red / Jet Charcoal / Emerald Green)
    // Full continuous strip from rInner to rOuter with NO middle band or dividing line
    ctx.beginPath();
    ctx.arc(cx, cy, rOuter, startAngle, endAngle, false);
    ctx.arc(cx, cy, rInner, endAngle, startAngle, true);
    ctx.closePath();
    ctx.fillStyle = getPocketColor(num);
    ctx.fill();

    // B. Gold Fret Divider Line between pockets
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(startAngle) * rInner, cy + Math.sin(startAngle) * rInner);
    ctx.lineTo(cx + Math.cos(startAngle) * rOuter, cy + Math.sin(startAngle) * rOuter);
    ctx.strokeStyle = "#e8c356";
    ctx.lineWidth = 4;
    ctx.stroke();

    // C. Number Typography - Placed directly along the outer edge
    ctx.save();
    const tx = cx + Math.cos(midAngle) * rText;
    const ty = cy + Math.sin(midAngle) * rText;
    ctx.translate(tx, ty);

    // Rotate so numbers are upright when viewed from player camera
    ctx.rotate(midAngle - Math.PI / 2);

    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    const isDoubleZero = num === "00";
    ctx.font = isDoubleZero
      ? "900 76px 'Arial Black', Impact, sans-serif"
      : "900 86px 'Arial Black', Impact, sans-serif";

    // Pure brilliant solid white fill
    ctx.fillStyle = "#ffffff";
    ctx.fillText(String(num), 0, 0);

    ctx.restore();
  }

  // 2. Concentric Metallic Gold Rings: Outer, Middle Dividing Ring, and Inner Base
  const drawRing = (radius, width, mainColor = "#ffd452", highlightColor = "#fff3c4") => {
    // Soft outer shadow/dark gold
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, TAU);
    ctx.strokeStyle = "#996d13";
    ctx.lineWidth = width + 2;
    ctx.stroke();

    // Main vibrant gold
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, TAU);
    ctx.strokeStyle = mainColor;
    ctx.lineWidth = width;
    ctx.stroke();

    // Specular highlight
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, TAU);
    ctx.strokeStyle = highlightColor;
    ctx.lineWidth = Math.max(1.5, width * 0.35);
    ctx.stroke();
  };

  const rMiddle = 865;

  drawRing(rOuter - 3, 5, "#ffd452", "#fff3c4");
  drawRing(rMiddle, 8, "#ffd452", "#fff3c4");
  drawRing(rInner + 2, 6, "#ffd452", "#fff3c4");

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 16;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.generateMipmaps = true;
  return texture;
}

// Generate burnished copper/mahogany texture for the central cone matching reference image
function createBurnishedConeTexture() {
  const canvas = document.createElement("canvas");
  canvas.width = 1024;
  canvas.height = 1024;
  const ctx = canvas.getContext("2d");

  const cx = 512;
  const cy = 512;
  const r = 512;

  // Rich copper-mahogany gradient
  const grad = ctx.createRadialGradient(cx, cy, 10, cx, cy, r);
  grad.addColorStop(0, "#8a4020");
  grad.addColorStop(0.35, "#5c2813");
  grad.addColorStop(0.70, "#381507");
  grad.addColorStop(1, "#1c0903");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 1024, 1024);

  // 16 radial panel segments with soft specular shine
  const segments = 16;
  for (let i = 0; i < segments; i++) {
    const a1 = (i / segments) * TAU;
    const a2 = ((i + 1) / segments) * TAU;

    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, r, a1, a2);
    ctx.closePath();

    ctx.fillStyle = i % 2 === 0 ? "rgba(255, 200, 140, 0.05)" : "rgba(0, 0, 0, 0.12)";
    ctx.fill();

    // Subtle seam line
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(cx + Math.cos(a1) * r, cy + Math.sin(a1) * r);
    ctx.strokeStyle = "rgba(0, 0, 0, 0.25)";
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }

  // Specular sheen ring
  ctx.beginPath();
  ctx.arc(cx, cy, 260, 0, TAU);
  ctx.strokeStyle = "rgba(255, 255, 255, 0.15)";
  ctx.lineWidth = 40;
  ctx.stroke();

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

// Generate polished mahogany texture for outer wooden basin
function createMahoganyBowlTexture() {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 512;
  const ctx = canvas.getContext("2d");

  const cx = 256;
  const cy = 256;
  const r = 256;

  const grad = ctx.createRadialGradient(cx, cy, 50, cx, cy, r);
  grad.addColorStop(0, "#d6a33c");
  grad.addColorStop(0.6, "#805016");
  grad.addColorStop(1, "#3a2006");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 512, 512);

  // Concentric wood grain rings
  for (let rad = 20; rad < r; rad += 6) {
    ctx.beginPath();
    ctx.arc(cx, cy, rad, 0, TAU);
    ctx.strokeStyle = "rgba(0, 0, 0, 0.04)";
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
}

// Cabinet & Outer Wooden Basin Component (Restored authentic 3D casino wheel)
function Cabinet() {
  const bowlTexture = useMemo(() => createMahoganyBowlTexture(), []);

  useEffect(() => {
    return () => bowlTexture.dispose();
  }, [bowlTexture]);

  const mahoganyMaterial = useMemo(() => {
    return new THREE.MeshStandardMaterial({
      map: bowlTexture,
      roughness: 0.20,
      metalness: 0.85,
    });
  }, [bowlTexture]);

  const brassMaterial = useMemo(() => {
    return new THREE.MeshStandardMaterial({
      color: "#ffc107", // True Gold Color
      metalness: 1.0,   // Fully metallic
      roughness: 0.15,  // Very smooth and reflective
    });
  }, []);

  const darkWallMaterial = useMemo(() => {
    return new THREE.MeshStandardMaterial({
      color: "#000000",
      roughness: 0.35,
      metalness: 0.25,
    });
  }, []);

  const greenFeltMaterial = useMemo(() => {
    return new THREE.MeshStandardMaterial({
      color: "#073d23",
      roughness: 0.94,
      metalness: 0.02,
    });
  }, []);

  // 8 Diamond brass deflectors on outer track wall
  const deflectors = useMemo(() => {
    const items = [];
    const count = 8;
    for (let i = 0; i < count; i++) {
      const angle = (i / count) * TAU;
      const r = 3.42;
      items.push({
        x: Math.sin(angle) * r,
        z: Math.cos(angle) * r,
        rotY: angle,
      });
    }
    return items;
  }, []);

  return (
    <group>
      {/* Outer Green Casino Felt Table Removed */}

      {/* Table Shadow Overlay - Soft tight shadow */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.84, 0]}>
        <ringGeometry args={[4.2, 4.6, 128]} />
        <meshBasicMaterial color="#000000" transparent opacity={0.15} />
      </mesh>

      {/* Main Outer Polished Wood Cabinet Ring */}
      <mesh position={[0, 0.16, 0]} receiveShadow castShadow>
        <cylinderGeometry args={[4.26, 4.42, 0.74, 128, 1, true]} />
        <primitive object={brassMaterial} attach="material" />
      </mesh>

      {/* Heavy Outer Wooden Rim Top (Lies flat on X-Z plane) */}
      <mesh position={[0, 0.53, 0]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow castShadow>
        <torusGeometry args={[4.18, 0.26, 24, 128]} />
        <primitive object={brassMaterial} attach="material" />
      </mesh>

      {/* Outer Brass Lip Rim (Lies flat on X-Z plane, framing the full-width strips) */}
      <mesh position={[0, 0.57, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <torusGeometry args={[3.90, 0.05, 16, 128]} />
        <primitive object={brassMaterial} attach="material" />
      </mesh>
    </group>
  );
}

// Rotating Number Rotor, Pocket Separators & Turned Gold Turret (Exact match to image_9330bb23.jpg)
function NumberRotor({ wheelRef, highlightNumber, sequence }) {
  const total = sequence.length;
  const step = TAU / total;

  // Single unified high-res rotor texture
  const rotorTexture = useMemo(() => createPhotorealisticRotorTexture(sequence), [sequence]);
  const coneTexture = useMemo(() => createBurnishedConeTexture(), []);

  useEffect(() => {
    return () => {
      rotorTexture.dispose();
      coneTexture.dispose();
    };
  }, [rotorTexture, coneTexture]);

  const brassMaterial = useMemo(() => {
    return new THREE.MeshStandardMaterial({
      color: "#ffc107",
      metalness: 1.0,
      roughness: 0.15,
    });
  }, []);

  const coneMaterial = useMemo(() => {
    return new THREE.MeshStandardMaterial({
      map: coneTexture,
      roughness: 0.18,
      metalness: 0.85,
    });
  }, [coneTexture]);

  return (
    <group ref={wheelRef}>
      {/* 1. Photorealistic Numbered Ring Surface - Full Strips from 1.86 to 3.86 */}
      <mesh position={[0, 0.585, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[1.86, 3.86, 128]} />
        <meshStandardMaterial
          map={rotorTexture}
          roughness={0.85}
          metalness={0.02}
        />
      </mesh>

      {/* 2. Concentric 3D Gleaming Golden Dividing Ring under the edge numbers */}
      <mesh position={[0, 0.59, 0]} rotation={[-Math.PI / 2, 0, 0]} castShadow>
        <torusGeometry args={[3.26, 0.026, 16, 128]} />
        <primitive object={brassMaterial} attach="material" />
      </mesh>

      {/* 3. Central Burnished Mahogany/Copper Cone */}
      <mesh position={[0, 0.65, 0]} castShadow receiveShadow>
        <coneGeometry args={[1.85, 0.44, 64]} />
        <primitive object={coneMaterial} attach="material" />
      </mesh>

      {/* 4. Precision Turned Golden Trophy Spindle (Exact match to reference image) */}
      <GoldenTrophySpindle material={brassMaterial} />
    </group>
  );
}

// Precision Turned Lathe Trophy Component (Matching image_9330bb23.jpg)
function GoldenTrophySpindle({ material }) {
  const trophyGeometry = useMemo(() => {
    const pts = [];
    // Flared pedestal base sitting flush on the cone
    pts.push(new THREE.Vector2(0.56, 0.00));
    pts.push(new THREE.Vector2(0.54, 0.025));
    pts.push(new THREE.Vector2(0.46, 0.055));
    pts.push(new THREE.Vector2(0.44, 0.085));
    // Bullnose stepped ring
    pts.push(new THREE.Vector2(0.40, 0.11));
    pts.push(new THREE.Vector2(0.42, 0.145));
    pts.push(new THREE.Vector2(0.40, 0.18));
    pts.push(new THREE.Vector2(0.32, 0.21));
    // Slender concave waist / trophy stem
    pts.push(new THREE.Vector2(0.23, 0.26));
    pts.push(new THREE.Vector2(0.18, 0.34));
    pts.push(new THREE.Vector2(0.17, 0.42));
    pts.push(new THREE.Vector2(0.20, 0.50));
    // Trophy chalice flare
    pts.push(new THREE.Vector2(0.28, 0.58));
    pts.push(new THREE.Vector2(0.38, 0.66));
    pts.push(new THREE.Vector2(0.40, 0.74));
    // Chalice upper lip
    pts.push(new THREE.Vector2(0.38, 0.78));
    pts.push(new THREE.Vector2(0.34, 0.81));
    // Stepped domed lid
    pts.push(new THREE.Vector2(0.35, 0.84));
    pts.push(new THREE.Vector2(0.28, 0.88));
    pts.push(new THREE.Vector2(0.22, 0.94));
    // Crown tier
    pts.push(new THREE.Vector2(0.16, 1.00));
    pts.push(new THREE.Vector2(0.17, 1.05));
    pts.push(new THREE.Vector2(0.14, 1.10));
    // Top finial spire
    pts.push(new THREE.Vector2(0.08, 1.15));
    pts.push(new THREE.Vector2(0.03, 1.21));
    pts.push(new THREE.Vector2(0.00, 1.25));

    const geom = new THREE.LatheGeometry(pts, 48);
    geom.computeVertexNormals();
    return geom;
  }, []);

  return (
    <group position={[0, 0.85, 0]}>
      {/* Turned Lathe Trophy Body */}
      <mesh geometry={trophyGeometry} castShadow receiveShadow>
        <primitive object={material} attach="material" />
      </mesh>

      {/* 4 Ornate Horizontal Handles on the Chalice Waist */}
      {[0, Math.PI / 2, Math.PI, (Math.PI * 3) / 2].map((hAngle, i) => (
        <group key={i} rotation={[0, hAngle, 0]} position={[0, 0.72, 0]}>
          {/* Main horizontal arm */}
          <mesh position={[0.26, 0, 0]} rotation={[0, 0, Math.PI / 2]} castShadow>
            <cylinderGeometry args={[0.032, 0.032, 0.24, 16]} />
            <primitive object={material} attach="material" />
          </mesh>
          {/* Accent ring */}
          <mesh position={[0.34, 0, 0]} rotation={[0, 0, Math.PI / 2]} castShadow>
            <torusGeometry args={[0.048, 0.016, 12, 24]} />
            <primitive object={material} attach="material" />
          </mesh>
          {/* Spherical finial tip */}
          <mesh position={[0.42, 0, 0]} castShadow>
            <sphereGeometry args={[0.055, 24, 24]} />
            <primitive object={material} attach="material" />
          </mesh>
        </group>
      ))}
    </group>
  );
}

// Complete Casino Roulette Spin Physics & Synchronized Settlement Engine
function Scene({ isSpinning, winningNumber, sequence, spinDuration = 4.0, onSettled }) {
  const total = sequence.length;
  const step = TAU / total;

  const wheelRef = useRef(null);
  const ballPivotRef = useRef(null);
  const ballRef = useRef(null);

  const stateRef = useRef("IDLE");
  const wheelAngle = useRef(0);
  const ballAngle = useRef(0);

  const wheelSpeed = useRef(0.28);
  const ballSpeed = useRef(0);

  const ballRadius = useRef(2.50);
  const ballHeight = useRef(0.725);

  const settleStartTime = useRef(0);
  const settleDuration = useRef(spinDuration || 4.2);
  const settleStartWheel = useRef(0);
  const settleStartBall = useRef(0);
  const settleTargetWheel = useRef(0);
  const settleTargetBall = useRef(0);
  const lockedWinningNumber = useRef(null);

  const [highlightedNum, setHighlightedNum] = useState(null);

  const settleTimeoutRef = useRef(null);

  const startSettling = useCallback((targetNum, customDur = 2.5) => {
    stateRef.current = "SETTLING";
    lockedWinningNumber.current = targetNum;

    const targetIdx = sequence.findIndex(
      (val) => String(val) === String(targetNum)
    );
    const validIdx = targetIdx >= 0 ? targetIdx : 0;
    const pocketTargetAngle = validIdx * step;

    const dur = customDur || 2.5;
    settleDuration.current = dur;
    settleStartTime.current = performance.now() / 1000;

    settleStartWheel.current = wheelAngle.current;
    settleStartBall.current = ballAngle.current;

    const totalWheelRot = Math.max(1.8, wheelSpeed.current) * (dur * 0.42);
    const finalWheel = settleStartWheel.current + totalWheelRot;
    settleTargetWheel.current = finalWheel;

    const baseBallTarget = finalWheel - pocketTargetAngle;
    let finalBall = settleStartBall.current;
    while (finalBall > baseBallTarget - Math.PI * 8) {
      finalBall -= TAU;
    }
    finalBall += (baseBallTarget - finalBall) % TAU;
    if (finalBall > settleStartBall.current - Math.PI * 6) {
      finalBall -= TAU;
    }
    settleTargetBall.current = finalBall;
  }, [sequence, step]);

  useEffect(() => {
    if (settleTimeoutRef.current) {
      clearTimeout(settleTimeoutRef.current);
      settleTimeoutRef.current = null;
    }

    if (isSpinning) {
      // Start active free spin around outer track
      stateRef.current = "FREE_SPIN";
      setHighlightedNum(null);
      wheelSpeed.current = 2.4;
      ballSpeed.current = -7.6;
      ballRadius.current = 3.65;
      ballHeight.current = 0.725;

      const totalDur = spinDuration || 6.0;
      const settleDur = 2.5;
      const freeSpinMs = Math.max(800, (totalDur - settleDur) * 1000);

      if (winningNumber !== null && winningNumber !== undefined) {
        settleTimeoutRef.current = setTimeout(() => {
          startSettling(winningNumber, settleDur);
        }, freeSpinMs);
      }
    } else {
      // When not spinning, wheel is completely STOPPED with ball resting in winning number pocket
      stateRef.current = "STOPPED";
      wheelSpeed.current = 0;
      ballSpeed.current = 0;
      ballRadius.current = 2.54;
      ballHeight.current = 0.725;

      if (winningNumber !== null && winningNumber !== undefined) {
        const targetIdx = sequence.findIndex(
          (val) => String(val) === String(winningNumber)
        );
        const validIdx = targetIdx >= 0 ? targetIdx : 0;
        const pocketTargetAngle = validIdx * step;
        ballAngle.current = wheelAngle.current - pocketTargetAngle;
        setHighlightedNum(winningNumber);
      }
    }

    return () => {
      if (settleTimeoutRef.current) {
        clearTimeout(settleTimeoutRef.current);
      }
    };
  }, [isSpinning, winningNumber, startSettling, sequence, step, spinDuration]);

  useFrame((_, delta) => {
    const now = performance.now() / 1000;

    if (stateRef.current === "IDLE" || stateRef.current === "STOPPED") {
      // Completely stopped: zero rotation, ball stationary in pocket
      wheelSpeed.current = 0;
      ballSpeed.current = 0;
      ballRadius.current = 2.54;
      ballHeight.current = 0.725;
    } else if (stateRef.current === "FREE_SPIN") {
      wheelAngle.current += wheelSpeed.current * delta;
      ballAngle.current += ballSpeed.current * delta;
      // Ball spins directly along the outer rim edge
      ballRadius.current = 3.65;
      ballHeight.current = 0.725;
      ballSpeed.current = THREE.MathUtils.lerp(ballSpeed.current, -6.8, delta * 0.5);
    } else if (stateRef.current === "SETTLING") {
      const elapsed = now - settleStartTime.current;
      const progress = Math.min(1, elapsed / settleDuration.current);

      const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
      const pEase = easeOutCubic(progress);

      wheelAngle.current = THREE.MathUtils.lerp(
        settleStartWheel.current,
        settleTargetWheel.current,
        pEase
      );

      ballAngle.current = THREE.MathUtils.lerp(
        settleStartBall.current,
        settleTargetBall.current,
        pEase
      );

      if (progress < 0.45) {
        const q = progress / 0.45;
        ballRadius.current = THREE.MathUtils.lerp(3.65, 2.70, q);
        ballHeight.current = 0.725;
      } else if (progress < 0.80) {
        const q = (progress - 0.45) / 0.35;
        const bounceOffset = Math.abs(Math.sin(q * Math.PI * 6)) * 0.045 * (1 - q);
        const radialWobble = Math.cos(q * Math.PI * 4) * 0.015 * (1 - q);

        ballRadius.current = THREE.MathUtils.lerp(2.70, 2.54, q) + radialWobble;
        ballHeight.current = 0.725 + bounceOffset;
      } else {
        const q = (progress - 0.80) / 0.20;
        const microBounce = Math.abs(Math.sin(q * Math.PI * 3)) * 0.012 * Math.pow(1 - q, 2);

        ballRadius.current = 2.54;
        ballHeight.current = 0.725 + microBounce;
      }

      if (progress >= 1 && stateRef.current !== "STOPPED") {
        stateRef.current = "STOPPED";

        const landed = lockedWinningNumber.current !== null
          ? lockedWinningNumber.current
          : (() => {
            const relAngle = (ballAngle.current - wheelAngle.current) % TAU;
            const normRel = (TAU - (relAngle < 0 ? relAngle + TAU : relAngle)) % TAU;
            const idx = Math.round(normRel / step) % total;
            return sequence[idx];
          })();

        setHighlightedNum(landed);
        onSettled?.(landed);
      }
    }

    if (wheelRef.current) {
      wheelRef.current.rotation.y = wheelAngle.current;
    }
    if (ballPivotRef.current) {
      ballPivotRef.current.rotation.y = ballAngle.current;
    }
    if (ballRef.current) {
      ballRef.current.position.set(ballRadius.current, ballHeight.current, 0);
    }
  });

  return (
    <>
      <Environment preset="city" />
      {/* Reduced manual lighting since Environment map provides global illumination */}
      <ambientLight intensity={0.3} />
      <directionalLight
        position={[0, 9, 6]}
        intensity={0.6}
        color="#fff4e0"
      />
      <pointLight position={[-6, 4, 3]} intensity={0.5} color="#ffd8a8" />
      <pointLight position={[6, 4, 3]} intensity={0.5} color="#ffd8a8" />
      <pointLight position={[0, 4, 6]} intensity={0.6} color="#ffe8cc" />

      <Cabinet />
      <NumberRotor
        wheelRef={wheelRef}
        highlightNumber={highlightedNum}
        sequence={sequence}
      />

      {/* Glossy Pure Ivory White Roulette Ball */}
      <group ref={ballPivotRef}>
        <mesh ref={ballRef} castShadow receiveShadow>
          <sphereGeometry args={[0.135, 32, 32]} />
          <meshPhysicalMaterial
            color="#ffffff"
            roughness={0.02}
            metalness={0.0}
            clearcoat={1.0}
            clearcoatRoughness={0.01}
            reflectivity={1.0}
          />
        </mesh>
      </group>
    </>
  );
}

// Main Exported Interactive 3D Roulette Component (Matching image_9330bb23.jpg)
export default function RouletteWheel3D({
  isSpinning = false,
  winningNumber = null,
  variant = "EUROPEAN",
  spinDuration = 4.0,
  onSettled,
}) {
  const [currentVariant, setCurrentVariant] = useState(variant);
  const [internalSpinning, setInternalSpinning] = useState(false);
  const [internalTarget, setInternalTarget] = useState(null);
  const [internalResult, setInternalResult] = useState(null);
  const containerRef = useRef(null);

  const isControlledByGame = typeof isSpinning === "boolean" && onSettled !== undefined;

  const sequence = useMemo(() => {
    return currentVariant === "AMERICAN" ? AMERICAN_WHEEL : EUROPEAN_WHEEL;
  }, [currentVariant]);

  const activeSpinning = isSpinning || internalSpinning;
  const activeWinningNumber = winningNumber !== null ? winningNumber : internalTarget;

  // Audio synchronization with spin state
  useEffect(() => {
    if (activeSpinning) {
      sounds?.playWheelSpinAudio?.();
    } else {
      sounds?.stopWheelSpinAudio?.();
    }
  }, [activeSpinning]);

  const handleSettled = useCallback(
    (num) => {
      setInternalResult(num);
      setInternalSpinning(false);
      onSettled?.(num);
    },
    [onSettled]
  );

  const handleStandaloneSpin = () => {
    if (activeSpinning) return;
    setInternalResult(null);
    setInternalSpinning(true);
    setInternalTarget(null);

    setTimeout(() => {
      const pick = sequence[Math.floor(Math.random() * sequence.length)];
      setInternalTarget(pick);
    }, 2200);
  };

  const getResultColorBadge = (num) => {
    if (num === null || num === undefined) return null;
    if (num === 0 || num === "00") return { text: "GREEN", bg: "#008544" };
    const n = Number(num);
    if (RED_NUMBERS.has(n)) return { text: "RED", bg: "#b8121f" };
    return { text: "BLACK", bg: "#131518" };
  };

  const badge = getResultColorBadge(internalResult);

  return (
    <div
      ref={containerRef}
      className="two-ring-roulette-shell"
      style={{ width: "100%", height: "100%", position: "relative", overflow: "visible" }}
    >
      {/* Vignette removed for clean overlap */}

      {/* 3D WebGL Canvas matching image_9330bb23.jpg */}
      <div
        className="two-ring-roulette-canvas"
        style={{
          position: "absolute",
          top: "-80px",
          bottom: "-80px",
          left: "0",
          right: "0",
          width: "100%",
          height: "auto",
        }}
      >
        <Canvas
          shadows
          dpr={[1, 1.75]}
          camera={{
            position: [0, 7.5, 4.8],
            fov: 65,
            near: 0.1,
            far: 60,
          }}
          gl={{
            antialias: true,
            alpha: true,
            powerPreference: "high-performance",
          }}
          onCreated={({ camera, gl }) => {
            camera.lookAt(0, 0.20, 0);
            gl.outputColorSpace = THREE.SRGBColorSpace;
            gl.toneMapping = THREE.ACESFilmicToneMapping;
            gl.toneMappingExposure = 0.85;
          }}
        >
          <Scene
            isSpinning={activeSpinning}
            winningNumber={activeWinningNumber}
            sequence={sequence}
            spinDuration={spinDuration}
            onSettled={handleSettled}
          />
        </Canvas>
      </div>

      {/* Standalone Control Overlay */}
      {!isControlledByGame && (
        <div
          style={{
            position: "absolute",
            bottom: "8px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 10,
            display: "flex",
            alignItems: "center",
            gap: "10px",
            pointerEvents: "auto",
          }}
        >
          <div
            style={{
              background: "rgba(10, 15, 12, 0.85)",
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(226, 182, 71, 0.4)",
              borderRadius: "20px",
              padding: "3px",
              display: "flex",
              gap: "4px",
            }}
          >
            <button
              onClick={() => !activeSpinning && setCurrentVariant("EUROPEAN")}
              disabled={activeSpinning}
              style={{
                padding: "4px 12px",
                borderRadius: "16px",
                fontSize: "11px",
                fontWeight: "700",
                border: "none",
                cursor: activeSpinning ? "not-allowed" : "pointer",
                background: currentVariant === "EUROPEAN"
                  ? "linear-gradient(180deg, #e2b647 0%, #aa820a 100%)"
                  : "transparent",
                color: currentVariant === "EUROPEAN" ? "#ffffff" : "#aaaaaa",
                transition: "all 0.2s ease",
              }}
            >
              🇪🇺 Single 0
            </button>
            <button
              onClick={() => !activeSpinning && setCurrentVariant("AMERICAN")}
              disabled={activeSpinning}
              style={{
                padding: "4px 12px",
                borderRadius: "16px",
                fontSize: "11px",
                fontWeight: "700",
                border: "none",
                cursor: activeSpinning ? "not-allowed" : "pointer",
                background: currentVariant === "AMERICAN"
                  ? "linear-gradient(180deg, #e2b647 0%, #aa820a 100%)"
                  : "transparent",
                color: currentVariant === "AMERICAN" ? "#ffffff" : "#aaaaaa",
                transition: "all 0.2s ease",
              }}
            >
              🇺🇸 Double 00
            </button>
          </div>

          <button
            onClick={handleStandaloneSpin}
            disabled={activeSpinning}
            style={{
              padding: "6px 24px",
              fontSize: "14px",
              fontWeight: "800",
              letterSpacing: "1px",
              color: "#ffffff",
              background: activeSpinning
                ? "linear-gradient(180deg, #555555 0%, #333333 100%)"
                : "linear-gradient(180deg, #e2b647 0%, #aa820a 100%)",
              border: "1.5px solid #ffe89e",
              borderRadius: "24px",
              boxShadow: activeSpinning
                ? "none"
                : "0 4px 14px rgba(226, 182, 71, 0.4)",
              cursor: activeSpinning ? "not-allowed" : "pointer",
              transition: "all 0.2s ease",
              textTransform: "uppercase",
            }}
          >
            {activeSpinning ? "Spinning..." : "SPIN"}
          </button>

          {internalResult !== null && !activeSpinning && (
            <div
              style={{
                background: "rgba(10, 15, 12, 0.90)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(226, 182, 71, 0.6)",
                borderRadius: "20px",
                padding: "4px 14px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                color: "#ffffff",
              }}
            >
              <span style={{ fontSize: "11px", color: "#e2b647", fontWeight: "700" }}>
                WIN: <strong>{internalResult}</strong>
              </span>
              {badge && (
                <span
                  style={{
                    background: badge.bg,
                    color: "#ffffff",
                    padding: "2px 8px",
                    borderRadius: "4px",
                    fontSize: "10px",
                    fontWeight: "800",
                  }}
                >
                  {badge.text}
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
